import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Bot, Copy, KeyRound, Pause, Play, ShieldOff, Plus, Trash2,
  AlertTriangle, ExternalLink, CheckCircle2, XCircle, Activity, Snowflake,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { agentsApi, formatApiError } from "../../lib/ledgerApi";
import AgentKeyRevealCard from "../../components/AgentKeyRevealCard";
import AgentEmbedTab from "../../components/AgentEmbedTab";

const StatusPill = ({ s }) => {
  const map = {
    active: "border-status-ok/40 bg-status-ok/10 text-status-ok",
    paused: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
    revoked: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
  };
  return (
    <span className={`px-1.5 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-widest ${map[s] || "border-border bg-muted"}`}>
      {s}
    </span>
  );
};

const IntentRow = ({ intent }) => {
  const map = {
    consumed: "text-status-ok",
    approved: "text-status-ok",
    pending: "text-amber-600 dark:text-amber-400",
    rejected: "text-status-tampered",
    failed: "text-status-tampered",
    expired: "text-muted-foreground",
  };
  return (
    <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs border-b border-border last:border-b-0">
      <div className="col-span-3 font-mono text-[11px] truncate">
        {new Date(intent.created_at).toLocaleString()}
      </div>
      <div className="col-span-3 truncate font-mono text-[11px]">
        → {intent.to_vpa}
      </div>
      <div className="col-span-2 font-mono text-right">
        ${intent.amount} <span className="text-muted-foreground">{intent.currency}</span>
      </div>
      <div className={`col-span-2 ${map[intent.status] || ""} text-[10px] uppercase tracking-widest`}>
        {intent.status}
      </div>
      <div className="col-span-2 text-right">
        {intent.linked_receipt_id && (
          <Link
            to={`/x402/receipt/${intent.linked_receipt_id}`}
            className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            rcpt <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
          </Link>
        )}
      </div>
    </div>
  );
};

export default function DeveloperAgentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [agent, setAgent] = useState(null);
  const [keys, setKeys] = useState([]);
  const [intents, setIntents] = useState([]);
  const [usage, setUsage] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [tab, setTab] = useState("overview"); // overview | incidents
  const [loading, setLoading] = useState(true);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const load = async () => {
    try {
      const [a, k, ints, inc] = await Promise.all([
        agentsApi.get(id),
        agentsApi.listKeys(id),
        agentsApi.recentPayments(50).then((all) => all.filter((i) => i.agent_id === id)),
        agentsApi.incidents(id, 50).catch(() => []),
      ]);
      setAgent(a);
      setKeys(k);
      setIntents(ints);
      setIncidents(inc);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [id]);

  const onTogglePause = async () => {
    try {
      if (agent.status === "paused") await agentsApi.resume(id);
      else await agentsApi.pause(id);
      await load();
      toast.success(agent.status === "paused" ? "Agent resumed" : "Agent paused");
    } catch (e) {
      toast.error("Toggle failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const onRevoke = async () => {
    setConfirmRevoke(false);
    try {
      await agentsApi.revoke(id);
      toast.success("Agent revoked", { description: "All keys and pending intents have been cancelled." });
      await load();
    } catch (e) {
      toast.error("Revoke failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const onMintKey = async () => {
    try {
      const r = await agentsApi.mintKey(id);
      // Wrap in same shape as create to reuse the reveal card
      setNewKey({ agent, api_key: r });
      await load();
    } catch (e) {
      toast.error("Mint failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const onRevokeKey = async (kid) => {
    try {
      await agentsApi.revokeKey(id, kid);
      toast.success("Key revoked");
      await load();
    } catch (e) {
      toast.error("Revoke failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  if (loading || !agent) {
    return <div className="px-6 md:px-10 py-10 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="max-w-6xl px-6 md:px-10 py-10 md:py-14" data-testid="agent-detail-page">
      <button
        onClick={() => nav("/app/developer")}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6"
        data-testid="back-to-developer"
      >
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to agents
      </button>

      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
            / Developer · Agent
          </div>
          <div className="flex items-center gap-3 mb-2">
            <Bot className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
            <h1 className="text-3xl tracking-tight font-medium">{agent.display_name}</h1>
            <StatusPill s={agent.status} />
          </div>
          <button
            data-testid="agent-did-copy"
            onClick={() => { navigator.clipboard.writeText(agent.did); toast.success("DID copied"); }}
            className="text-xs font-mono text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
          >
            {agent.did} <Copy className="w-3 h-3" strokeWidth={1.5} />
          </button>
          {agent.description && (
            <div className="mt-3 text-sm text-muted-foreground max-w-xl">{agent.description}</div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={onTogglePause}
            disabled={agent.status === "revoked"}
            data-testid="agent-toggle-pause"
            className="rounded-sm"
          >
            {agent.status === "paused"
              ? <><Play className="w-3 h-3 mr-1.5" strokeWidth={1.5} /> Resume</>
              : <><Pause className="w-3 h-3 mr-1.5" strokeWidth={1.5} /> Pause</>}
          </Button>
          <Button
            variant="outline"
            onClick={() => setConfirmRevoke(true)}
            disabled={agent.status === "revoked"}
            data-testid="agent-revoke-btn"
            className="rounded-sm border-status-tampered/60 text-status-tampered hover:bg-status-tampered hover:text-background"
          >
            <ShieldOff className="w-3 h-3 mr-1.5" strokeWidth={1.5} /> Kill switch
          </Button>
        </div>
      </div>

      {agent.auto_freeze_reason && agent.status === "paused" && (
        <div data-testid="auto-freeze-banner" className="mb-6 border border-amber-500/40 bg-amber-500/10 rounded-sm p-3 text-xs text-amber-700 dark:text-amber-300 flex gap-2">
          <Snowflake className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
          <div>
            <strong>Auto-frozen.</strong> {agent.auto_freeze_reason}
            {" "}Resume from the toolbar above once you've reviewed the incidents tab.
          </div>
        </div>
      )}

      {/* Tab nav */}
      <div className="mb-6 border-b border-border flex gap-6 text-xs uppercase tracking-widest">
        <button
          onClick={() => setTab("overview")}
          data-testid="tab-overview"
          className={`pb-2 border-b-2 transition-colors ${tab === "overview" ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >
          Overview
        </button>
        <button
          onClick={() => setTab("incidents")}
          data-testid="tab-incidents"
          className={`pb-2 border-b-2 transition-colors flex items-center gap-2 ${tab === "incidents" ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >
          Incidents
          {incidents.length > 0 && (
            <span className="px-1.5 py-0.5 border border-border rounded-sm text-[9px] font-mono normal-case tracking-normal">
              {incidents.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab("embed")}
          data-testid="tab-embed"
          className={`pb-2 border-b-2 transition-colors ${tab === "embed" ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >
          Embed
        </button>
      </div>

      {tab === "incidents" ? (
        <IncidentsTimeline incidents={incidents} />
      ) : tab === "embed" ? (
        <AgentEmbedTab agentId={agent.id} agent={agent} />
      ) : (
        <>
      {/* Policy */}
      <div className="mb-8 border border-border rounded-sm bg-card p-5" data-testid="agent-policy-card">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Policy</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-mono">
          <PolicyCell label="Per-tx cap" value={`$${agent.policy.per_tx_cap_usd}`} />
          <PolicyCell label="Daily cap" value={`$${agent.policy.daily_cap_usd}`} />
          <PolicyCell label="Weekly cap" value={`$${agent.policy.weekly_cap_usd}`} />
          <PolicyCell label="Monthly cap" value={`$${agent.policy.monthly_cap_usd}`} />
          <PolicyCell label="Approve above" value={`$${agent.policy.require_human_approval_above_usd}`} />
          <PolicyCell label="Allowlist" value={agent.policy.merchant_allowlist?.length || "any"} />
          <PolicyCell label="Blocklist" value={agent.policy.merchant_blocklist?.length || "—"} />
          <PolicyCell label="Categories" value={agent.policy.category_allowlist?.length || "any"} />
        </div>
      </div>

      {/* API keys */}
      <div className="mb-8 border border-border rounded-sm bg-card overflow-hidden" data-testid="agent-keys-card">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">API keys</div>
          <Button
            size="sm"
            variant="outline"
            onClick={onMintKey}
            disabled={agent.status === "revoked"}
            data-testid="mint-key-btn"
            className="rounded-sm h-7 text-[10px] uppercase tracking-widest"
          >
            <Plus className="w-3 h-3 mr-1" strokeWidth={1.5} /> Mint key
          </Button>
        </div>
        {keys.length === 0 ? (
          <div className="p-5 text-xs text-muted-foreground">No keys yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {keys.map((k) => (
              <div key={k.id} className="px-5 py-3 grid grid-cols-12 gap-2 items-center text-xs">
                <div className="col-span-1">
                  <KeyRound className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <div className="col-span-3 font-mono">
                  neopay_ag_{k.key_prefix}…
                </div>
                <div className="col-span-3 text-muted-foreground font-mono text-[11px]">
                  {k.scope.join(", ")}
                </div>
                <div className="col-span-3 text-muted-foreground font-mono text-[11px]">
                  {new Date(k.created_at).toLocaleDateString()}
                  {k.last_used_at && (
                    <span className="ml-2 text-[10px]">· last {new Date(k.last_used_at).toLocaleTimeString()}</span>
                  )}
                </div>
                <div className="col-span-2 text-right">
                  {k.revoked_at ? (
                    <span className="text-[10px] uppercase tracking-widest text-status-tampered">revoked</span>
                  ) : (
                    <button
                      onClick={() => onRevokeKey(k.id)}
                      data-testid={`revoke-key-${k.id}`}
                      className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-status-tampered inline-flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" strokeWidth={1.5} /> revoke
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent payments */}
      <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="agent-payments-card">
        <div className="px-3 py-3 border-b border-border text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Recent payments
        </div>
        {intents.length === 0 ? (
          <div className="p-5 text-xs text-muted-foreground">No payments yet.</div>
        ) : (
          <div>
            <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
              <div className="col-span-3">When</div>
              <div className="col-span-3">To</div>
              <div className="col-span-2 text-right">Amount</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Receipt</div>
            </div>
            {intents.map((it) => <IntentRow key={it.id} intent={it} />)}
          </div>
        )}
      </div>
        </>
      )}

      <Dialog open={confirmRevoke} onOpenChange={setConfirmRevoke}>
        <DialogContent data-testid="revoke-confirm-dialog" className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-medium text-status-tampered">
              <ShieldOff className="w-4 h-4" strokeWidth={1.5} /> Revoke {agent.display_name}?
            </DialogTitle>
            <DialogDescription>
              This is the kill switch — irreversible. All API keys are revoked, all
              pending intents are cancelled, and the agent can never transact again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRevoke(false)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={onRevoke}
              data-testid="revoke-confirm-submit"
              className="rounded-sm bg-status-tampered text-background hover:opacity-90"
            >
              <ShieldOff className="w-3 h-3 mr-1.5" strokeWidth={1.5} /> Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {newKey && (
        <AgentKeyRevealCard
          data={newKey}
          onClose={() => setNewKey(null)}
          onView={() => setNewKey(null)}
        />
      )}
    </div>
  );
}

function PolicyCell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-0.5">{label}</div>
      <div>{value}</div>
    </div>
  );
}

function IncidentsTimeline({ incidents }) {
  if (!incidents.length) {
    return (
      <div data-testid="incidents-empty" className="border border-border rounded-sm bg-card p-10 text-center text-sm text-muted-foreground">
        No incidents recorded. Policy violations, fraud blocks, and status
        transitions will appear here.
      </div>
    );
  }
  const iconFor = (e) => {
    if (e.type === "audit") {
      if (e.action?.includes("revoke")) return ShieldOff;
      if (e.action === "auto_freeze") return Snowflake;
      if (e.action?.startsWith("set_status")) return Activity;
      return Activity;
    }
    return e.kind === "fraud_block" ? AlertTriangle : XCircle;
  };
  const colorFor = (e) => {
    if (e.type === "audit") {
      if (e.action?.includes("revoke")) return "text-status-tampered";
      if (e.action === "auto_freeze") return "text-amber-600 dark:text-amber-400";
      return "text-muted-foreground";
    }
    return e.auto_froze ? "text-amber-600 dark:text-amber-400" : "text-status-tampered";
  };
  return (
    <div data-testid="incidents-timeline" className="border border-border rounded-sm bg-card overflow-hidden">
      <div className="px-5 py-3 border-b border-border text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        Timeline · breaches + status transitions
      </div>
      <div className="divide-y divide-border">
        {incidents.map((e, i) => {
          const Icon = iconFor(e);
          return (
            <div key={i} data-testid={`incident-row-${i}`} className="px-5 py-3 grid grid-cols-12 gap-3 items-start text-xs">
              <div className="col-span-1 pt-0.5">
                <Icon className={`w-3.5 h-3.5 ${colorFor(e)}`} strokeWidth={1.5} />
              </div>
              <div className="col-span-3 font-mono text-[11px] text-muted-foreground">
                {new Date(e.at).toLocaleString()}
              </div>
              <div className="col-span-3 font-mono text-[11px] uppercase tracking-widest">
                {e.type === "audit" ? e.action : e.kind || "breach"}
              </div>
              <div className="col-span-5">
                <div className="font-mono text-[11px]">
                  {e.code || e.action}
                  {e.auto_froze && (
                    <span className="ml-2 px-1 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-sm text-[9px] uppercase tracking-widest">
                      triggered freeze
                    </span>
                  )}
                </div>
                {e.message && (
                  <div className="text-muted-foreground text-[11px] mt-0.5">{e.message}</div>
                )}
                {e.reason && (
                  <div className="text-muted-foreground text-[11px] mt-0.5">{e.reason}</div>
                )}
                {e.actor && (
                  <div className="text-[10px] text-muted-foreground/80 mt-0.5 font-mono">by {e.actor}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
